import classNames from "classnames";
import { useState } from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";
import { Button } from "../../../../components/buttons/button";
import { ContainerCard } from "../../../../components/cards/containerCard";
import {
  InformationCard,
  InformationCardNoIcon,
} from "../../../../components/cards/infoCard";
import { AssignOfficialModal } from "../../../../components/modals/app_modals/assignOfficialModal";
import { ManageRoleModal } from "../../../../components/modals/app_modals/manageRoleModal";
import { SuccessModal } from "../../../../components/modals/app_modals/successModal";
import { TitleSub } from "../../../../components/typography/titleSub";
import { useModalControl } from "../../../../customHooks/useModalControl";
import { useGetCooperativeOfficialsQuery } from "../../../../redux/features/cooperative/cooperative.api";
import { SingleCooperativeOfficial } from "../../../../redux/features/cooperative/cooperative.type";
import { useGetCooperativeRolesQuery } from "../../../../redux/features/settings/settings.api";
import { SingleRole } from "../../../../redux/features/settings/settings.type";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../utils/constants";

const SingleOfficialComponent = ({
  role,
  official,
  onAssign,
  className = "",
}: {
  role: SingleRole;
  official?: SingleCooperativeOfficial;
  onAssign: () => void;
  className?: string;
}) => {
  return (
    <div
      className={`flex  items-center gap-4 flex-wrap sm:flex-nowrap ${className}`}
    >
      <div className="flex-1 min-w-full sm:min-w-max">
        <p className="font-medium text-pry-dark-green-300 text-sm capitalize">
          {role.Name}
        </p>
        <div className="flex-1 flex flex-wrap gap-1 items-center text-pry-dark-green-300/60 font-medium">
          {!!official ? (
            <>
              <span className="">{official.MemberEmail}</span>

              <span>•</span>

              {!official.isVerified ? (
                <Button
                  type="button"
                  text="Resend Invite"
                  className="!w-fit !px-0 !py-0 "
                  onClick={() => {}}
                  disabled={false}
                  variation="plain"
                />
              ) : (
                <span>
                  {official.Member.FirstName} {official.Member.LastName}
                </span>
              )}
            </>
          ) : (
            <p className="">No official assigned</p>
          )}
        </div>
      </div>

      {!!official && (
        <p
          className={classNames(
            "py-1 border w-fit h-fit border-neut-300 px-2 rounded-3xl font-medium text-xs",
            {
              "text-neut-700 bg-[#F7F7F7]": !official.isVerified,
              "text-pry-green-200 bg-[#F1F9F6]": official.isVerified,
            }
          )}
        >{`• ${official?.isVerified ? "Accepted" : "Awaiting"}`}</p>
      )}

      <Button
        type="button"
        text={!!official ? "Re-assign" : "Assign"}
        onClick={() => {
          onAssign();
        }}
        size="small"
        className="!w-fit ml-auto"
        variation="secondary"
        icon="plus"
        iconClassName="text-pry-dark-green-300"
      />
    </div>
  );
};

export const CooperativeOfficials = () => {
  const [selectedRole, setSelectedRole] = useState<SingleRole>();
  const [createdRole, setCreatedRole] = useState<SingleRole>();

  const { modal, handleModalClose, handleModalOpen, modalOpen } =
    useModalControl();

  const {
    data: roles,
    isFetching: fetchingRoles,
    refetch: refetchRoles,
  } = useGetCooperativeRolesQuery(
    { limit: 100, offset: 0 },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: officials,
    isFetching: fetchingOfficials,
    refetch: refetchOfficials,
  } = useGetCooperativeOfficialsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });

  return (
    <>
      <div className="flex flex-row flex-wrap width-lim:flex-nowrap w-full gap-2 md:gap-6">
        <ContainerCard className="w-full width-lim:max-w-[653px] width-lim:min-h-[635px] order-last width-lim:order-first flex flex-col gap-6 !px-0">
          <div className="px-4 md:px-8">
            <TitleSub
              title="Manage Cooperative Officials"
              subtitle="Assign and manage officials who will control your cooperative activities"
            />
          </div>

          <hr className="" />

          <div className="px-4 md:px-8 sticky top-0 z-10">
            <InformationCardNoIcon
              className=""
              info={
                <>
                  To edit or delete roles, go to the “Role & Approvals” or{" "}
                  <Link
                    to="/app/settings?tab=roles_and_approvals"
                    className="text-pry-green-200 font-bold"
                  >
                    Click Here
                  </Link>
                </>
              }
            />
          </div>

          <div className="flex flex-col">
            {fetchingOfficials || fetchingRoles
              ? [1, 2, 3, 4].map((v) => {
                  return (
                    <div
                      key={`${v}`}
                      className="border-b first:pt-0 py-4  px-4 md:px-8"
                    >
                      <Skeleton
                        width={"140px"}
                        height={"20px"}
                        baseColor={SKELETON_BASE_COLOR}
                        highlightColor={SKELETON_HIGHLIGHT_COLOR}
                        className="rounded-md"
                      />

                      <div className="flex items-center gap-2 justify-between">
                        <Skeleton
                          width={"200px"}
                          height={"20px"}
                          baseColor={SKELETON_BASE_COLOR}
                          highlightColor={SKELETON_HIGHLIGHT_COLOR}
                          className="rounded-md"
                        />

                        <Skeleton
                          width={"120px"}
                          height={"32px"}
                          baseColor={SKELETON_BASE_COLOR}
                          highlightColor={SKELETON_HIGHLIGHT_COLOR}
                          className="rounded-md"
                          containerClassName="ml-auto max-md:hidden"
                        />
                      </div>
                    </div>
                  );
                })
              : undefined}

            {!fetchingOfficials &&
            !fetchingRoles &&
            ![0, undefined].includes(roles?.data?.results?.length)
              ? roles?.data?.results?.map((role) => {
                  return (
                    <SingleOfficialComponent
                      key={`${role.id}`}
                      role={role}
                      official={officials?.data?.find(
                        (off) => off?.Role?.id === role.id
                      )}
                      onAssign={() => {
                        setSelectedRole(role);
                        handleModalOpen("assignOfficialModal");
                      }}
                      className="border-b first:pt-0 py-4  px-4 md:px-8"
                    />
                  );
                })
              : undefined}
          </div>

          {!fetchingOfficials &&
            !fetchingRoles &&
            ![0, undefined].includes(roles?.data?.results?.length) && (
              <Button
                text="Add Role"
                icon="plus"
                className="!rounded-full !w-[136px] !ml-auto !mt-4 mr-4 md:mr-8"
                onClick={() => {
                  setSelectedRole(undefined);
                  handleModalOpen("manageRoleModal");
                }}
              />
            )}
        </ContainerCard>

        <InformationCard
          info={
            <p>
              <span className="text-pry-green-200 font-medium">
                Cooperative Officials{" "}
              </span>
              are the executives that coordinate the day to day activities of
              the cooperative.
            </p>
          }
          className="width-lim:!max-w-[345px] h-fit width-lim:sticky-top"
        />
      </div>

      {selectedRole && (
        <AssignOfficialModal
          isModalOpen={modalOpen && modal === "assignOfficialModal"}
          onModalClose={() => {
            setSelectedRole(undefined);
            setCreatedRole(undefined);
            handleModalClose();
          }}
          successCallback={() => {
            refetchOfficials();
            setSelectedRole(undefined);
            setCreatedRole(undefined);
            handleModalOpen("assignRoleSuccessModal");
          }}
          role={selectedRole}
          official={officials?.data?.find(
            (off) => off?.Role?.id === selectedRole.id
          )}
        />
      )}

      <SuccessModal
        isModalOpen={modalOpen && modal === "assignRoleSuccessModal"}
        onModalClose={() => {
          handleModalClose();
          setCreatedRole(undefined);
          setSelectedRole(undefined);
        }}
        message={"Invitation Sent successfully"}
        subText={
          <span>
            Kindly inform your official to join with the invitation link sent
            directly to their email address
          </span>
        }
      >
        <Button
          type="button"
          variation="plain"
          onClick={() => {
            setCreatedRole(undefined);
            setSelectedRole(undefined);
            handleModalClose();
          }}
          text="Dismiss"
          className="!mt-9"
        />
      </SuccessModal>

      <ManageRoleModal
        isModalOpen={modal === "manageRoleModal" && modalOpen}
        onModalClose={() => {
          setSelectedRole(undefined);
          setCreatedRole(undefined);
          handleModalClose();
        }}
        successCallback={(created) => {
          refetchRoles();

          setCreatedRole(created);
          setSelectedRole(created);
          handleModalOpen("createRoleSuccessModal");
        }}
        role={undefined}
      />

      <SuccessModal
        isModalOpen={modalOpen && modal === "createRoleSuccessModal"}
        onModalClose={() => {
          setCreatedRole(undefined);
          setSelectedRole(undefined);
          handleModalClose();
        }}
        message={"Role created successfully"}
        subText={
          <span>
            You have created the role of{" "}
            <span className="font-bold text-pry-dark-green-300/80">
              {createdRole?.Name}
            </span>
            . You can update this role whenever there’s a need to.
          </span>
        }
      >
        <div className="flex flex-col gap-3 mt-8 w-full">
          <Button
            type="button"
            variation="primary"
            onClick={() => {
              handleModalOpen("assignOfficialModal");
            }}
            text="Assign Official"
            className=""
          />

          <Button
            type="button"
            variation="secondary"
            onClick={() => {
              setCreatedRole(undefined);
              setSelectedRole(undefined);
              handleModalOpen("manageRoleModal");
            }}
            text="Create Another Role"
            className=""
          />
        </div>
      </SuccessModal>
    </>
  );
};
